/**
 * Theme runtime.
 *
 * Applies a theme by setting `data-theme` on <html>. The actual design
 * tokens for each theme live in `src/styles/theme.css` — this file only
 * decides which theme is active and persists that choice to localStorage.
 *
 * To add a new theme:
 *   1. Add a `[data-theme="<name>"]` block in src/styles/theme.css.
 *   2. Append the name (and label) to THEMES below.
 *   3. The picker UI will pick it up automatically once it reads from THEMES.
 *
 * Usage:
 *   import { applyTheme, getStoredTheme, THEMES } from '@/lib/theme';
 *   applyTheme('dark');                  // apply + persist
 *   applyTheme(getStoredTheme() ?? 'light');
 */

export type ThemeName = 'light' | 'dark' | 'hc';

export interface ThemeOption {
  name: ThemeName;
  label: string;
  description: string;
}

// Only Light is exposed in the picker for now. Dark + HC token blocks still
// exist in global-theme.css and work end-to-end (set data-theme="dark" on
// <html> manually to try them), but they need more contrast polish before
// shipping to users. Uncomment the two lines below when ready.
export const THEMES: readonly ThemeOption[] = [
  { name: 'light', label: 'Light', description: 'Clean white surfaces.' },
  // { name: 'dark',  label: 'Dark',          description: 'Forest-ink dark mode.' },
  // { name: 'hc',    label: 'High contrast', description: 'Black + white for accessibility.' },
] as const;

const STORAGE_KEY = 'gc.theme.v1';
const DEFAULT_THEME: ThemeName = 'light';

const isThemeName = (v: unknown): v is ThemeName =>
  typeof v === 'string' && THEMES.some(t => t.name === v);

/**
 * Apply a theme to the document root and persist the choice.
 * Falls back silently if `window` / `localStorage` aren't available.
 */
export function applyTheme(name: ThemeName): void {
  if (typeof document === 'undefined') return;
  const target = isThemeName(name) ? name : DEFAULT_THEME;
  document.documentElement.setAttribute('data-theme', target);
  // Legacy attributes kept in sync for any CSS still scoped to them.
  document.documentElement.setAttribute('data-chat-theme', target === 'light' ? '' : target);
  document.documentElement.setAttribute('data-task-theme', target === 'light' ? '' : target);
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, target);
    }
  } catch {
    /* quota / private mode — ignore */
  }
}

/** Read the persisted theme, or null if none chosen yet. */
export function getStoredTheme(): ThemeName | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isThemeName(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Decide the initial theme. We only honour a stored preference whose name
 * is currently exposed in THEMES — otherwise fall back to the default
 * (light). This keeps the app on the active picker option even if Dark or
 * HC were chosen earlier (when the picker exposed them).
 */
export function loadInitialTheme(): ThemeName {
  const stored = getStoredTheme();
  const allowed = stored && THEMES.some(t => t.name === stored) ? stored : null;
  const initial: ThemeName = allowed ?? DEFAULT_THEME;
  applyTheme(initial);
  return initial;
}
